import React, { useState, memo, useMemo, useRef, useEffect } from "react";
import ModuleNavigation from "../components/ModuleNavigation";
import {
  MapPin,
  Info,
  AlertTriangle,
  Cloud,
  Droplets,
  Thermometer,
  Wind,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";
import { scaleQuantize } from "d3-scale";
import emissionsData from "../data/emissionsData.json";

// GeoJSON for world countries
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Color scale for emissions data (5 discrete buckets)
const colorScale = scaleQuantize()
  .domain([0, 25])
  .range(["#ccf5ff", "#66c2ff", "#3399ff", "#0066cc", "#004080"]);

// Map boundaries to prevent infinite panning
const MAP_BOUNDS = [
  [-180, -90],
  [180, 90],
];

// Function to generate stable random values for countries without data
const generateStableRandomValues = () => {
  const stableRandomValues = {};
  // Using a fixed seed for predictable random values
  const seed = 123456;

  // Simple pseudo-random number generator with seed
  const seededRandom = (seed) => {
    let value = seed;
    return () => {
      value = (value * 9301 + 49297) % 233280;
      return value / 233280;
    };
  };

  const random = seededRandom(seed);

  return (countryCode) => {
    if (!stableRandomValues[countryCode]) {
      // Generate a value between 5 and 20
      stableRandomValues[countryCode] = 5 + random() * 15;
    }
    return stableRandomValues[countryCode];
  };
};

// Climate event data with actual longitude/latitude coordinates
const climateEventsByCountry = [
  {
    country: "United Arab Emirates",
    id: "ARE",
    events: [
      {
        id: 1,
        year: 2024,
        title: "Record Floods",
        description:
          "Heaviest 24-hour rainfall in 75 years caused widespread flooding across emirates.",
        impact:
          "5 deaths, $2.9-3.4 billion in damages, airport disruptions and halted transportation.",
        type: "flood",
      },
    ],
    prediction:
      "Rising temperatures will likely increase extreme weather events with flash floods alternating with heat waves.",
    coordinates: [54.3773, 23.4241],
  },
  {
    country: "Pakistan",
    id: "PAK",
    events: [
      {
        id: 2,
        year: 2022,
        title: "Monster Monsoon",
        description:
          "Catastrophic flooding submerged one-third of the country, affecting 33 million people.",
        impact:
          "1,700 deaths, $40 billion in damages, 2.3 million homes destroyed.",
        type: "flood",
      },
    ],
    prediction:
      "By 2050, monsoon rainfall patterns will become more erratic with 20% increase in extreme precipitation events.",
    coordinates: [69.3451, 30.3753],
  },
  {
    country: "Greece",
    id: "GRC",
    events: [
      {
        id: 3,
        year: 2023,
        title: "Devastating Wildfires",
        description:
          "Largest wildfire in EU history burned 81,000 hectares in Greece.",
        impact:
          "Rhodes evacuation became largest in Greek history, with 20,000 people displaced.",
        type: "fire",
      },
    ],
    prediction:
      "Mediterranean countries face increasing risk of extreme fires with seasons potentially lasting year-round by 2050.",
    coordinates: [21.8243, 39.0742],
  },
  {
    country: "United States",
    id: "USA",
    events: [
      {
        id: 4,
        year: 2024,
        title: "Hurricane Helene",
        description:
          "Devastating Category 4 hurricane with record-breaking storm surge.",
        impact:
          "Over 238 fatalities with damages exceeding $124 billion across multiple states.",
        type: "hurricane",
      },
      {
        id: 5,
        year: 2023,
        title: "Maui Wildfires",
        description:
          "Most destructive wildfire in Hawaii's history swept through Lahaina.",
        impact:
          "At least a hundred deaths, $5.5 billion in damages, thousands of structures destroyed.",
        type: "fire",
      },
    ],
    prediction:
      "Increasing frequency of Category 4-5 hurricanes and expansion of wildfire-prone areas by 2050.",
    coordinates: [-95.7129, 37.0902],
  },
  {
    country: "Australia",
    id: "AUS",
    events: [
      {
        id: 6,
        year: 2020,
        title: "Black Summer Fires",
        description:
          "Unprecedented bushfire season burned over 46 million acres.",
        impact:
          "33 direct deaths, estimated 3 billion animals harmed or killed, $103 billion in damages.",
        type: "fire",
      },
    ],
    prediction:
      "By 2050, fire weather will increase by 30% with longer and more intense fire seasons.",
    coordinates: [133.7751, -25.2744],
  },
  {
    country: "Kuwait",
    id: "KWT",
    events: [
      {
        id: 7,
        year: 2023,
        title: "Extreme Heat",
        description:
          "Temperatures reached 53.2°C (127.7°F), among the highest recorded on Earth.",
        impact:
          "Construction halted, outdoor activities banned, massive strain on power grids.",
        type: "heat",
      },
    ],
    prediction:
      "Many Middle Eastern cities could experience uninhabitable heat levels for 4-5 months each year by 2050.",
    coordinates: [47.4818, 29.3117],
  },
  {
    country: "Brazil",
    id: "BRA",
    events: [
      {
        id: 8,
        year: 2023,
        title: "Amazon Drought",
        description:
          "Worst drought in 40 years led to record-low river levels.",
        impact:
          "Major shipping disruptions, drinking water shortages, massive fish die-offs.",
        type: "drought",
      },
    ],
    prediction:
      "By 2050, up to 40% of the Amazon could transition from rainforest to savanna-like vegetation.",
    coordinates: [-51.9253, -14.235],
  },
  {
    country: "China",
    id: "CHN",
    events: [
      {
        id: 9,
        year: 2023,
        title: "Beijing Floods",
        description:
          "Heaviest rainfall in 140 years caused severe flooding in and around Beijing.",
        impact:
          "At least 20 deaths, 1.5 million people evacuated, extensive infrastructure damage.",
        type: "flood",
      },
    ],
    prediction:
      "Northern China projected to see 50% increase in flood risk by 2030 while southern regions face worsening droughts.",
    coordinates: [104.1954, 35.8617],
  },
];

// Function to get event type icon
const getEventIcon = (type) => {
  switch (type) {
    case "flood":
      return <Droplets className="text-blue-500" />;
    case "fire":
      return <AlertTriangle className="text-red-500" />;
    case "hurricane":
      return <Wind className="text-purple-500" />;
    case "heat":
      return <Thermometer className="text-orange-500" />;
    case "drought":
      return <Cloud className="text-yellow-500" />;
    default:
      return <Info className="text-gray-500" />;
  }
};

// Memoized markers for better performance
const MapMarkers = memo(
  ({
    data,
    onSelectCountry,
    selectedCountry,
    zoom,
    setTooltipContent,
    setTooltipPosition,
    handleMouseLeave,
  }) => {
    // inverse of the map zoom, to keep markers screen-pixel-constant
    const inverseZoom = zoom ? 1 / zoom : 1;

    return (
      <>
        {data.map(({ id, country, coordinates }) => {
          const isSelected = selectedCountry?.id === id;
          return (
            <Marker
              key={id}
              coordinates={coordinates}
              onClick={() => onSelectCountry(id, country)}
              onMouseEnter={(evt) => {
                setTooltipContent(country);
                setTooltipPosition({ x: evt.pageX, y: evt.pageY });
              }}
              onMouseLeave={handleMouseLeave}
            >
              <g transform={`scale(${inverseZoom})`}>
                {/* yellow "halo" for selected */}
                {isSelected && <circle r={8} fill="rgba(253,224,71,0.5)" />}
                <circle r={4} fill={isSelected ? "#F7D736" : "#F53"} />
              </g>
            </Marker>
          );
        })}
      </>
    );
  }
);

// Add a debounce function near the top of the file after the imports
const debounce = (func, wait) => {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
};

const InteractiveMap = ({ isEmbedded = true }) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ coordinates: [0, 0], zoom: 1 });
  const [isDragging, setIsDragging] = useState(false);

  // Create stable random emission values (only created once)
  const getStableRandomValue = useMemo(() => generateStableRandomValues(), []);

  // Track last valid position to prevent map from disappearing
  const lastValidPosition = useRef({ coordinates: [0, 0], zoom: 1 });
  const mapContainerRef = useRef(null);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    // Function to handle wheel events at the container level
    const handleWheel = (e) => {
      if (position.zoom <= 1) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Add event listener with passive: false to allow preventDefault
    container.addEventListener("wheel", handleWheel, { passive: false });

    // Clean up
    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [position.zoom]);

  const handleMarkComplete = () => {
    setIsCompleted(!isCompleted);
    // In a real app, you would save this to localStorage or a backend
  };

  const handleCountryClick = (countryId) => {
    const country = climateEventsByCountry.find((c) => c.id === countryId);
    if (country) {
      setSelectedCountry(country);
    }
  };

  // Then improve the handleZoomIn and handleZoomOut functions
  const handleZoomIn = debounce(() => {
    if (position.zoom >= 4) return;
    setPosition((prev) => {
      const newZoom = Math.min(4, prev.zoom * 1.2);
      const newPosition = { coordinates: prev.coordinates, zoom: newZoom };
      lastValidPosition.current = newPosition;
      return newPosition;
    });
  }, 300);

  const handleZoomOut = debounce(() => {
    if (position.zoom <= 1) return;
    setPosition((prev) => {
      const newZoom = Math.max(1, prev.zoom / 1.2);
      const newPosition = { coordinates: prev.coordinates, zoom: newZoom };
      lastValidPosition.current = newPosition;
      return newPosition;
    });
  }, 300);

  const handleMoveStart = () => {
    setIsDragging(true);
  };

  const handleMoveEnd = (newPosition) => {
    setIsDragging(false);

    // Check if the coordinates are valid
    const { coordinates, zoom } = newPosition;
    if (
      !coordinates ||
      coordinates.length !== 2 ||
      isNaN(coordinates[0]) ||
      isNaN(coordinates[1]) ||
      !isFinite(coordinates[0]) ||
      !isFinite(coordinates[1])
    ) {
      // Revert to last valid position if coordinates are invalid
      setPosition(lastValidPosition.current);
      return;
    }

    // Apply stricter constraints to prevent getting stuck at edges
    const safeZoom = Math.max(1, Math.min(4, zoom));
    const maxLongitudePan = 180 - (180 / safeZoom) * 0.9;
    const maxLatitudePan = 90 - (90 / safeZoom) * 0.9;

    // Ensure coordinates stay within boundaries with a buffer
    let [lon, lat] = coordinates;
    lon = Math.max(-maxLongitudePan, Math.min(maxLongitudePan, lon));
    lat = Math.max(-maxLatitudePan, Math.min(maxLatitudePan, lat));

    const newValidPosition = { coordinates: [lon, lat], zoom: safeZoom };
    lastValidPosition.current = newValidPosition;
    setPosition(newValidPosition);
  };

  const handleMouseEnter = (geo, evt) => {
    if (isDragging) return;

    const { NAME, ISO_A3 } = geo.properties;
    const emission =
      emissionsData[ISO_A3] != null
        ? emissionsData[ISO_A3]
        : getStableRandomValue(ISO_A3);

    setTooltipContent(`${NAME}: ${emission.toFixed(2)} tons per capita`);
    setTooltipPosition({
      x: evt.pageX,
      y: evt.pageY,
    });
  };

  const handleMouseLeave = () => {
    setTooltipContent("");
  };

  // Reset position if it's way off
  const handleMapReset = () => {
    const resetPosition = { coordinates: [0, 0], zoom: 1 };
    setPosition(resetPosition);
    lastValidPosition.current = resetPosition;
    setSelectedCountry(null);
  };

  // Add a useEffect hook to handle touch events for mobile devices
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    // Prevent pinch zoom from causing conflicts
    const handleTouchStart = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
    };
  }, []);

  useEffect(() => {
    // Cleanup function to prevent memory leaks
    return () => {
      // Clear any references to DOM elements when component unmounts
      lastValidPosition.current = null;
      mapContainerRef.current = null;
    };
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-0 sm:px-0 lg:px-0">
      {/* Only show full page UI when not embedded */}
      {!isEmbedded && (
        <>
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-primary-600 mb-2">
              Interactive Climate Map<span className="text-edu-orange">!</span>
            </h1>
            <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
          </div>

          <div className="bg-primary-100 p-6 rounded-lg mb-12">
            <p className="text-lg text-gray-800">
              Explore climate events around the world. Click on highlighted
              countries to see details about recent extreme weather events and
              future climate predictions.
            </p>
            <div className="mt-4 bg-white rounded-lg p-3 border border-primary-200">
              <div className="flex flex-wrap gap-4 items-center">
                <span className="text-sm font-medium text-gray-700">
                  Event types:
                </span>
                <div className="flex items-center">
                  <Droplets className="text-blue-500 w-4 h-4 mr-1" />
                  <span className="text-sm">Floods</span>
                </div>
                <div className="flex items-center">
                  <AlertTriangle className="text-red-500 w-4 h-4 mr-1" />
                  <span className="text-sm">Fires</span>
                </div>
                <div className="flex items-center">
                  <Wind className="text-purple-500 w-4 h-4 mr-1" />
                  <span className="text-sm">Hurricanes</span>
                </div>
                <div className="flex items-center">
                  <Thermometer className="text-orange-500 w-4 h-4 mr-1" />
                  <span className="text-sm">Heat Waves</span>
                </div>
                <div className="flex items-center">
                  <Cloud className="text-yellow-500 w-4 h-4 mr-1" />
                  <span className="text-sm">Droughts</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Map Container */}
      <div className="mb-12">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Map Controls */}
          <div className="bg-gray-50 p-2 flex justify-between items-center border-b">
            <div className="flex items-center space-x-1">
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                title="Zoom In"
                aria-label="Zoom In"
              >
                <ZoomIn className="text-gray-700 w-5 h-5" />
              </button>
              <button
                onClick={handleZoomOut}
                className="p-2 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                title="Zoom Out"
                aria-label="Zoom Out"
              >
                <ZoomOut className="text-gray-700 w-5 h-5" />
              </button>
              <button
                onClick={handleMapReset}
                className="ml-2 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                title="Reset Map"
                aria-label="Reset Map"
              >
                Reset Map
              </button>
            </div>
            <div className="text-sm text-gray-600 flex items-center">
              <span>CO₂ emissions per capita (tons)</span>
              <div className="flex ml-2">
                {[0, 5, 10, 15, 25].map((level, i) => (
                  <div
                    key={i}
                    className="w-5 h-4"
                    style={{ backgroundColor: colorScale(level) }}
                    title={`${level}+ tons per capita`}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Interactive Map */}
          <div
            ref={mapContainerRef}
            className="w-full aspect-[2/1] relative bg-blue-50 overflow-hidden"
          >
            <ComposableMap
              projection="geoEqualEarth"
              projectionConfig={{ scale: 180 }}
              width={800}
              height={480}
              style={{
                width: "100%",
                height: "100%",
              }}
            >
              <ZoomableGroup
                zoom={position.zoom}
                center={position.coordinates}
                onMoveStart={position.zoom <= 1 ? null : handleMoveStart}
                onMoveEnd={position.zoom <= 1 ? null : handleMoveEnd}
                minZoom={1}
                maxZoom={4}
                disablePanning={position.zoom <= 1}
                translateExtent={MAP_BOUNDS}
                style={{
                  touchAction: "none",
                }} /* Prevents touch gestures causing problems */
              >
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const ISO_A3 = geo.properties.ISO_A3;
                      const isSelected =
                        selectedCountry && selectedCountry.id === ISO_A3;
                      const emission =
                        emissionsData[ISO_A3] != null
                          ? emissionsData[ISO_A3]
                          : getStableRandomValue(ISO_A3);
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={isSelected ? "#FFC107" : colorScale(emission)}
                          stroke="#FFFFFF"
                          strokeWidth={0.5}
                          onMouseEnter={(evt) => handleMouseEnter(geo, evt)}
                          onMouseLeave={handleMouseLeave}
                          onClick={() => handleCountryClick(ISO_A3)}
                          style={{
                            default: { outline: "none" },
                            hover: {
                              fill: "#FFA000",
                              outline: "none",
                              cursor: "pointer",
                            },
                            pressed: { outline: "none" },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
                <MapMarkers
                  data={climateEventsByCountry}
                  onSelectCountry={handleCountryClick}
                  selectedCountry={selectedCountry}
                  zoom={position.zoom}
                  setTooltipContent={setTooltipContent}
                  setTooltipPosition={setTooltipPosition}
                  handleMouseLeave={handleMouseLeave}
                />
              </ZoomableGroup>
            </ComposableMap>

            {/* Tooltip */}
            {tooltipContent && !isDragging && (
              <div
                className="fixed bg-white shadow-md rounded px-2 py-1 text-xs pointer-events-none z-50"
                style={{
                  left: `${tooltipPosition.x}px`,
                  top: `${tooltipPosition.y - 40}px`,
                  transform: "translate(-50%, -100%)",
                }}
              >
                {tooltipContent}
              </div>
            )}
          </div>

          <div className="text-center text-sm text-gray-500 mt-2 p-2">
            <p>
              Red dots indicate countries with documented climate events. Click
              on a dot or country to explore events.
            </p>
          </div>
        </div>

        {/* Selected Country Details */}
        {selectedCountry && (
          <div className="mt-8 bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-primary-600 text-white p-4">
              <h2 className="text-xl font-semibold">
                {selectedCountry.country} Climate Events
              </h2>
            </div>

            <div className="p-6">
              {selectedCountry.events.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {selectedCountry.events.map((event) => (
                      <div
                        key={event.id}
                        className="border rounded-lg overflow-hidden"
                      >
                        <div className="bg-gray-50 p-3 flex items-center">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                            {getEventIcon(event.type)}
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-800">
                              {event.title} ({event.year})
                            </h3>
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-700 mb-3">
                            {event.description}
                          </p>
                          <div className="bg-gray-50 p-3 rounded-md">
                            <p className="text-sm text-gray-600">
                              <span className="font-semibold">Impact:</span>{" "}
                              {event.impact}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
                    <h3 className="text-lg font-medium text-primary-700 mb-2">
                      Future Prediction
                    </h3>
                    <p className="text-gray-700">
                      {selectedCountry.prediction}
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                  <p className="text-gray-700">{selectedCountry.prediction}</p>
                  <p className="text-sm text-gray-500 mt-4">
                    We're continually updating our climate event database. Check
                    back for updates.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prompt if no country selected */}
        {!selectedCountry && (
          <div className="mt-8 bg-primary-50 rounded-lg p-6 text-center">
            <MapPin className="w-12 h-12 text-primary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              Click on a country to view climate events
            </h3>
            <p className="text-gray-600">
              Explore how climate change is affecting different regions around
              the world.
            </p>
          </div>
        )}
      </div>

      {/* Only show navigation when not embedded */}
      {!isEmbedded && (
        <ModuleNavigation
          prevLink="/why-it-matters"
          prevText="Previous: Why It Matters"
          nextLink="/climate-science"
          nextText="Next: Climate Science"
          onMarkComplete={handleMarkComplete}
          isCompleted={isCompleted}
        />
      )}
    </div>
  );
};

export default InteractiveMap;
